/**
 * Capacity Calculations
 *
 * Core module for calculating usable storage capacity accounting for:
 * - TB to TiB conversion (manufacturer base-10 vs ZFS base-2)
 * - Parity loss (RAIDZ vs mirror)
 * - P+1 padding (RAIDZ allocation efficiency)
 * - ZFS overhead (labels, metaslabs)
 * - Slop space (adaptive reservation)
 * - Compression
 *
 * Reference: DESIGN.md §5, Math Verification Plan §2
 * Test Cases: Math Verification Plan §3 (Test Case 1)
 */

import type {
  StorageTopology,
  DriveParameters,
  ZFSParameters,
  CapacityResults,
  RaidLevelMeta,
  CapacityUtilization,
} from '../types';

// ============================================================================
// CONSTANTS
// ============================================================================

/** TB to TiB conversion factor: (1000/1024)^4 */
export const TB_TO_TIB = Math.pow(1000 / 1024, 4);

/**
 * RAID Level Metadata and Resilver Penalty Factors
 *
 * RESILVER PENALTY EXPLANATION:
 *
 * The resilverPenalty factor represents the effective throughput reduction during
 * resilver operations compared to peak sequential speed. Different RAID levels
 * have different penalties due to their reconstruction algorithms.
 *
 * === MIRROR (0.80 = 80% efficiency) ===
 * - Mirrors resilver by sequentially reading from the surviving mirror copy
 * - Minimal computational overhead (simple copy operation)
 * - Can achieve ~80% of peak sequential speed
 * - Best resilver performance of all RAID levels
 *
 * === RAIDZ1 (0.50 = 50% efficiency) ===
 * - Must reconstruct data by reading ALL surviving disks and computing XOR parity
 * - Single parity requires: data = sum of all surviving disks
 * - Significant I/O amplification: reading N-1 disks to rebuild 1 disk
 * - Computational overhead for parity calculation reduces throughput
 * - Typical resilver speed: ~50% of peak sequential
 *
 * === RAIDZ2 (0.45 = 45% efficiency) ===
 * - Dual parity requires even more computation than RAIDZ1
 * - Must read and verify two independent parity calculations
 * - Reed-Solomon math is more CPU-intensive
 * - Slightly slower than RAIDZ1 despite similar I/O pattern
 * - Typical resilver speed: ~45% of peak sequential
 *
 * === RAIDZ3 (0.40 = 40% efficiency) ===
 * - Triple parity requires maximum computational overhead
 * - Three parity calculations per block reconstructed
 * - Highest I/O amplification and CPU utilization
 * - Slowest resilver performance
 * - Typical resilver speed: ~40% of peak sequential
 *
 * RESEARCH SOURCES:
 * - OpenZFS source code analysis of raidz_* reconstruction algorithms
 * - Community benchmarks at servethehome.com and r/zfs
 * - ZFS on-disk format specification (block pointer, parity layout)
 * - Real-world resilver measurements on 7200 RPM HDDs (10-20TB range)
 *
 * WRITE PENALTY EXPLANATION:
 * The writePenalty factor represents how many physical disk operations are required
 * for each logical write operation:
 * - Mirror: 2x (write to both mirrors)
 * - RAIDZ1: 4x (read old data + old parity, write new data + new parity)
 * - RAIDZ2: 6x (read old data + 2 old parity, write new data + 2 new parity)
 * - RAIDZ3: 8x (read old data + 3 old parity, write new data + 3 new parity)
 */

/** RAID level metadata */
export const RAID_LEVEL_META: Record<string, RaidLevelMeta> = {
  mirror: {
    parityDrives: 1, // After 1 drive fails, need 1 more failure for data loss
    maxFailures: 1, // Per mirror pair (N-1 overall)
    writePenalty: 2,
    resilverPenalty: 0.80,
    minDrives: 2,
    maxDrives: Infinity, // Practical limit depends on controller
  },
  raidz1: {
    parityDrives: 1,
    maxFailures: 1,
    writePenalty: 4,
    resilverPenalty: 0.50,
    minDrives: 3,
    maxDrives: 8, // Discouraged above 6
  },
  raidz2: {
    parityDrives: 2,
    maxFailures: 2,
    writePenalty: 6,
    resilverPenalty: 0.45,
    minDrives: 4,
    maxDrives: 12, // Discouraged above 10
  },
  raidz3: {
    parityDrives: 3,
    maxFailures: 3,
    writePenalty: 8,
    resilverPenalty: 0.40,
    minDrives: 5,
    maxDrives: 16, // Discouraged above 14
  },
};

/** ZFS overhead per disk (labels + bootloader) in MiB */
export const DISK_OVERHEAD_MIB = 4.5;

/** Metaslab size in GiB (typical) */
export const METASLAB_SIZE_GIB = 16;

// ============================================================================
// CONVERSION FUNCTIONS
// ============================================================================

/**
 * Convert manufacturer TB (base-10) to ZFS TiB (base-2)
 *
 * Formula: Capacity_TiB = Capacity_TB × (1000/1024)^4
 *
 * @param capacityTB - Drive capacity in TB (manufacturer rating)
 * @returns Capacity in TiB (actual ZFS capacity)
 *
 * @example
 * tbToTib(18) // ≈ 16.37 TiB
 */
export function tbToTib(capacityTB: number): number {
  return capacityTB * TB_TO_TIB;
}

/**
 * Convert TiB to TB (base-10)
 *
 * @param capacityTiB - Capacity in TiB
 * @returns Capacity in TB (manufacturer rating)
 */
export function tibToTb(capacityTiB: number): number {
  return capacityTiB / TB_TO_TIB;
}

// ============================================================================
// CORE CAPACITY CALCULATIONS
// ============================================================================

/**
 * Calculate raw capacity before any deductions
 *
 * Formula: raw = drives_per_vdev × vdev_count × drive_size_TiB
 *
 * @param topology - Storage topology
 * @param driveSizeTiB - Drive size in TiB
 * @returns Raw capacity in TiB
 */
export function calculateRawCapacity(
  topology: StorageTopology,
  driveSizeTiB: number
): number {
  const { drivesPerVdev, vdevCount } = topology;
  return drivesPerVdev * vdevCount * driveSizeTiB;
}

/**
 * Calculate capacity after parity loss
 *
 * For RAIDZ: usable_drives = drives_per_vdev - parity_drives
 * For mirrors: usable = raw / mirror_width
 *
 * CRITICAL: For mirrors, we use usable_drives = vdev_count (since each vdev
 * contributes 1 drive worth of capacity in a 2-way mirror)
 *
 * @param rawTiB - Raw capacity in TiB
 * @param topology - Storage topology
 * @returns Capacity after parity in TiB
 */
export function calculateAfterParity(
  rawTiB: number,
  topology: StorageTopology
): number {
  const { raidLevel, drivesPerVdev } = topology;
  const meta = RAID_LEVEL_META[raidLevel];

  if (raidLevel === 'mirror') {
    // For mirrors, usable capacity = vdev_count (assuming 2-way mirrors)
    // Each mirror vdev contributes 1 drive worth of capacity
    const mirrorWidth = drivesPerVdev;
    return rawTiB / mirrorWidth;
  }

  // For RAIDZ
  const usableDrives = drivesPerVdev - meta.parityDrives;
  const usableRatio = usableDrives / drivesPerVdev;
  return rawTiB * usableRatio;
}

/**
 * Calculate P+1 padding overhead
 *
 * ZFS requires allocations to be multiples of P+1 sectors to avoid
 * unallocatable fragments. This creates overhead that varies based on
 * recordsize and vdev width.
 *
 * Formula: S_total = ceil((S_data + P) / (P+1)) × (P+1)
 *
 * This is a heuristic estimation. Actual efficiency is tracked via
 * vdev_deflate_ratio in ZFS.
 *
 * @param afterParityTiB - Capacity after parity in TiB
 * @param topology - Storage topology
 * @param recordsizeKiB - Recordsize in KiB (optional, default 128)
 * @returns Capacity after P+1 padding in TiB
 */
export function calculateP1Padding(
  afterParityTiB: number,
  topology: StorageTopology,
  recordsizeKiB: number = 128
): number {
  const { raidLevel, drivesPerVdev } = topology;

  if (raidLevel === 'mirror') {
    // Mirrors don't have P+1 padding
    return afterParityTiB;
  }

  // RAIDZ P+1 padding heuristic
  // Wider vdevs and smaller recordsizes create more overhead
  // Parity level affects padding but simplified heuristic used here

  // Simplified heuristic: 1-3% overhead depending on width and recordsize
  // In reality, this requires complex sector-level calculation
  const widthFactor = Math.log(drivesPerVdev) / Math.log(2); // Log scale
  const recordFactor = recordsizeKiB < 128 ? 1.02 : 1.0;
  const paddingOverhead = 1.0 + (widthFactor * 0.005 * recordFactor);

  return afterParityTiB / paddingOverhead;
}

/**
 * Calculate ZFS overhead (labels, metaslabs)
 *
 * Includes:
 * - Partition and label overhead: ~4.5 MiB per disk
 * - Metaslab rounding: Incomplete metaslab at end of disk
 *
 * @param afterPaddingTiB - Capacity after padding in TiB
 * @param totalDrives - Total number of drives
 * @returns Capacity after ZFS overhead in TiB
 */
export function calculateZFSOverhead(
  afterPaddingTiB: number,
  totalDrives: number
): number {
  // Label overhead per disk in TiB
  const labelOverheadTiB = (DISK_OVERHEAD_MIB * totalDrives) / (1024 * 1024);

  // Metaslab loss (heuristic: ~0.5% of remaining space)
  const metaslabOverhead = afterPaddingTiB * 0.005;

  return afterPaddingTiB - labelOverheadTiB - metaslabOverhead;
}

/**
 * Calculate slop space deduction
 *
 * Formula: slop_space = pool_capacity / 2^spa_slop_shift
 * Default spa_slop_shift = 5 → 1/32 = 3.125%
 *
 * Caps: Min 128 MiB, Max 128 GiB, Never exceeds 50% of pool
 *
 * @param afterOverheadTiB - Capacity after overhead in TiB
 * @param slopPercent - Slop space percentage (default 3.125)
 * @returns Capacity after slop in TiB
 */
export function calculateSlopSpace(
  afterOverheadTiB: number,
  slopPercent: number = 3.125
): number {
  // Convert percentage to decimal
  const slopRatio = slopPercent / 100;

  // Calculate slop space in TiB
  let slopSpace = afterOverheadTiB * slopRatio;

  // Apply caps:
  // - For small pools: cap slop at 128 MiB (don't overcharge tiny pools)
  // - For large pools: cap slop at 128 GiB (don't reserve too much)
  // - Never exceed 50% of pool capacity
  const minSlopCap = 128 / (1024 * 1024); // 128 MiB in TiB
  const maxSlopCap = 128 / 1024; // 128 GiB in TiB
  const maxAllowedPool = afterOverheadTiB / 2; // Never exceed 50%

  // Cap the slop space: use calculated value, but limit to [minCap, maxCap]
  slopSpace = Math.min(slopSpace, maxSlopCap);
  slopSpace = Math.min(slopSpace, maxAllowedPool);

  // For very small pools where calculated slop < minCap, use minCap
  slopSpace = Math.max(slopSpace, minSlopCap);

  return afterOverheadTiB - slopSpace;
}

/**
 * Calculate final usable capacity after compression
 *
 * Formula: final_usable = usable_after_slop × compression_ratio
 *
 * @param afterSlopTiB - Capacity after slop in TiB
 * @param compressionRatio - Compression ratio (1.0 = none, 2.0 = 2:1)
 * @returns Final usable capacity in TiB
 */
export function calculateFinalUsable(
  afterSlopTiB: number,
  compressionRatio: number
): number {
  return afterSlopTiB * compressionRatio;
}

// ============================================================================
// MAIN CAPACITY CALCULATION ORCHESTRATOR
// ============================================================================

/**
 * Calculate complete capacity breakdown
 *
 * Orchestrates all capacity calculations in the correct sequence:
 * 1. Raw capacity
 * 2. After parity loss
 * 3. After P+1 padding
 * 4. After ZFS overhead
 * 5. After slop space
 * 6. Final usable (with compression)
 * 7. Reserve free space limit
 *
 * Test Case Reference: Math Verification Plan §3, Test Case 1
 * - 6× 18TB drives, RAIDZ2, ashift=12, slop=3.125%, compression=1.0
 * - Expected: ~65 TiB final usable
 *
 * @param topology - Storage topology
 * @param drive - Drive parameters
 * @param zfs - ZFS parameters
 * @param utilization - Capacity utilization (fill level and free space limit)
 * @returns Complete capacity breakdown
 */
export function calculateCapacity(
  topology: StorageTopology,
  drive: DriveParameters,
  zfs: ZFSParameters,
  utilization: CapacityUtilization
): CapacityResults {
  // Step 1: TB to TiB conversion
  const driveSizeTiB = tbToTib(drive.driveSizeTB);

  // Step 2: Raw capacity
  const rawTiB = calculateRawCapacity(topology, driveSizeTiB);

  // Step 3: After parity
  const afterParityTiB = calculateAfterParity(rawTiB, topology);
  const parityLoss = rawTiB - afterParityTiB;

  // Step 4: After P+1 padding
  const afterPaddingTiB = calculateP1Padding(
    afterParityTiB,
    topology,
    zfs.recordsize
  );
  const paddingLoss = afterParityTiB - afterPaddingTiB;

  // Step 5: After ZFS overhead
  const totalDrives = topology.drivesPerVdev * topology.vdevCount;
  const afterOverheadTiB = calculateZFSOverhead(afterPaddingTiB, totalDrives);
  const overheadLoss = afterPaddingTiB - afterOverheadTiB;

  // Step 6: After slop space
  const afterSlopTiB = calculateSlopSpace(afterOverheadTiB, zfs.slopPercent);
  const slopLoss = afterOverheadTiB - afterSlopTiB;

  // Step 7: Final usable (with compression)
  const finalUsableTiB = calculateFinalUsable(
    afterSlopTiB,
    zfs.compressionRatio
  );

  // Step 8: Reserve free space limit
  // User wants to keep X% free for performance reasons (e.g., 20% free = 80% usable)
  // This is the space that should remain unused
  const reservedSpace = finalUsableTiB * (utilization.freeSpaceLimitPercent / 100);
  const practicalUsableTiB = finalUsableTiB - reservedSpace;

  // Calculate percentages
  const usablePercent = (finalUsableTiB / rawTiB) * 100;
  const lossPercent = 100 - usablePercent;
  const practicalUsablePercent = (practicalUsableTiB / rawTiB) * 100;

  return {
    rawTiB,
    afterParityTiB,
    afterPaddingTiB,
    afterOverheadTiB,
    afterSlopTiB,
    finalUsableTiB,
    usablePercent,
    lossPercent,
    losses: {
      parity: parityLoss,
      padding: paddingLoss,
      overhead: overheadLoss,
      slop: slopLoss,
      reserved: reservedSpace,
    },
    reservedTiB: reservedSpace,
    practicalUsableTiB,
    practicalUsablePercent,
  };
}
